import { Col, Select, Spin } from 'antd'
import { FC, useEffect, useMemo, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import PerDropzone from '../../../../components/custom_components/DragAndDrop'
import PerFlexRow from '../../../../components/custom_components/FlexRow'
import { PerModal } from '../../../../components/custom_components/Modal'
import { showNotification } from '../../../../components/custom_components/Notification/notification'
import PerSpacer from '../../../../components/custom_components/Spacer'
import PerText from '../../../../components/custom_components/Text'
import { importSelectableNationalContractReferenceLevels } from '../../../../graphql/nationalContractReference/MutationFunctions'
import { ExcelManager } from '../../../../shared/xlsx/ExcelManager'
import { ImportNationalContractReferenceInput, NationalContractReferenceWithTypeDto } from '../../../../types'
import { parseNationalContractReferenceExcelImport } from './nationalContractReferenceExcelImport'
import {
  downloadNationalContractReferenceTemplate,
  NATIONAL_CONTRACT_REFERENCE_IMPORT_ALL,
  NationalContractReferenceImportScope,
} from './nationalContractReferenceExcelTemplate'
import './index.less'
import { IconIndicator } from '../../../../components/custom_components/IconIndicator/IconIndicator'

const { Option } = Select

const MAX_ALLOWED_FILE_SIZE = 1048576

type DropZoneProcessing = {
  active: boolean
  isProcessing?: boolean
  text?: string
  errorMessage?: string
}

interface Props {
  visible: boolean
  references: NationalContractReferenceWithTypeDto[]
  onClose: () => void
}

export const HrSupportNationalContractReferenceImportModal: FC<Props> = ({ visible, references, onClose }) => {
  const { t } = useTranslation()
  const [selectedScope, setSelectedScope] = useState<NationalContractReferenceImportScope | undefined>(undefined)
  const [dropZoneError, setDropZoneError] = useState<string | undefined>(undefined)
  const [dropZoneProcessing, setDropZoneProcessing] = useState<DropZoneProcessing>({
    active: false,
    isProcessing: false,
  })
  const [filename, setFilename] = useState<string | undefined>(undefined)
  const [importedData, setImportedData] = useState<ImportNationalContractReferenceInput | undefined>(undefined)
  const [isConfirmingImportData, setIsConfirmingImportData] = useState(false)
  const statusBoxRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const isError = !!dropZoneProcessing.errorMessage
    const isSuccess = dropZoneProcessing.active && !dropZoneProcessing.isProcessing && !dropZoneProcessing.errorMessage

    if ((!isError && !isSuccess) || !statusBoxRef.current) {
      return
    }

    statusBoxRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  }, [dropZoneProcessing.active, dropZoneProcessing.errorMessage, dropZoneProcessing.isProcessing])

  const resetImportState = () => {
    setDropZoneError(undefined)
    setDropZoneProcessing({
      active: false,
      isProcessing: false,
    })
    setFilename(undefined)
    setImportedData(undefined)
    setIsConfirmingImportData(false)
  }

  useEffect(() => {
    if (visible) {
      setSelectedScope(undefined)
      resetImportState()
    }
  }, [visible])

  const handleClose = () => {
    onClose()
  }

  const handleScopeChange = (value: NationalContractReferenceImportScope | undefined) => {
    setSelectedScope(value)
    resetImportState()
  }

  const handleDownloadTemplate = () => {
    if (!selectedScope) {
      return
    }

    downloadNationalContractReferenceTemplate(references, selectedScope, t)
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onDrop = (uploadedFile: any) => {
    if (!selectedScope) {
      return
    }

    let hasError = false
    if (!uploadedFile) {
      hasError = true
      setDropZoneError(t('rankingView.excel.importModal.errors.noFile'))
    } else if (!uploadedFile.type) {
      hasError = true
      setDropZoneError(t('rankingView.excel.importModal.errors.noType'))
    } else if (!uploadedFile.path) {
      hasError = true
      setDropZoneError(t('rankingView.excel.importModal.errors.noPath'))
    } else {
      const extension = uploadedFile.path.substring(uploadedFile.path.lastIndexOf('.') + 1, uploadedFile.path.length)
      if (extension !== 'xlsx') {
        hasError = true
        setDropZoneError(t('rankingView.excel.importModal.errors.extension'))
      } else {
        importExcelFile(uploadedFile)
      }
    }

    if (hasError) {
      setDropZoneProcessing({
        active: false,
        isProcessing: false,
      })
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const importExcelFile = async (uploadedFile: any) => {
    if (!selectedScope) {
      return
    }

    setDropZoneProcessing({
      active: true,
      isProcessing: true,
      text: t('companySettingsView.nationalContractReferenceTab.importModal.processingMessage'),
    })
    setImportedData(undefined)

    let transformedFilename = uploadedFile.path
    const indexOfSlash = uploadedFile.path.indexOf('/')
    if (indexOfSlash >= 0) {
      transformedFilename = transformedFilename.substring(indexOfSlash + 1)
    }
    setFilename(transformedFilename)

    try {
      const sheets = await ExcelManager.getInstance().importExcelFileWithSheets(uploadedFile)
      const parsedImport = parseNationalContractReferenceExcelImport(sheets, selectedScope, references, t)

      if (!parsedImport.success) {
        setDropZoneProcessing({
          active: true,
          isProcessing: false,
          errorMessage: parsedImport.error,
        })
        return
      }

      setImportedData(parsedImport.input)
      setDropZoneProcessing({
        active: true,
        isProcessing: false,
        text: t('companySettingsView.nationalContractReferenceTab.importModal.successMessage'),
      })
    } catch {
      setDropZoneProcessing({
        active: false,
        isProcessing: false,
        errorMessage: t('companySettingsView.nationalContractReferenceTab.importModal.errors.unknown'),
      })
    }
  }

  const handleConfirm = async () => {
    if (!importedData) {
      return
    }

    setIsConfirmingImportData(true)

    const { errors } = await importSelectableNationalContractReferenceLevels(importedData)

    setIsConfirmingImportData(false)

    if (errors?.length) {
      setDropZoneProcessing({
        active: true,
        isProcessing: false,
        errorMessage:
          errors[0]?.extensions?.validationErrors?.[0]?.message ||
          t('companySettingsView.nationalContractReferenceTab.importModal.errors.unknown'),
      })
      return
    }

    showNotification({
      type: 'success',
      message: t('companySettingsView.nationalContractReferenceTab.importModal.notifications.success.title'),
      description: t('companySettingsView.nationalContractReferenceTab.importModal.notifications.success.description'),
    })
    handleClose()
  }

  const isConfirmDisabled =
    !dropZoneProcessing.active || dropZoneProcessing.isProcessing || !!dropZoneProcessing.errorMessage || !importedData

  const confirmDisabledTooltip = useMemo(() => {
    if (!isConfirmDisabled || dropZoneProcessing.isProcessing || isConfirmingImportData) {
      return undefined
    }

    if (dropZoneProcessing.errorMessage) {
      return t('companySettingsView.nationalContractReferenceTab.importModal.confirmDisabledTooltips.validationError')
    }

    if (!selectedScope) {
      return t('companySettingsView.nationalContractReferenceTab.importModal.confirmDisabledTooltips.noScope')
    }

    return t('companySettingsView.nationalContractReferenceTab.importModal.confirmDisabledTooltips.noFile')
  }, [dropZoneProcessing.errorMessage, dropZoneProcessing.isProcessing, isConfirmDisabled, isConfirmingImportData, selectedScope, t])

  return (
    <PerModal
      destroyOnClose
      centered
      size="small"
      title={{
        heading: t('companySettingsView.nationalContractReferenceTab.importModal.heading'),
      }}
      visible={visible}
      onCancel={handleClose}
      footer={{
        cancelBtn: { onClick: handleClose },
        customBtns: [
          {
            text: t('companySettingsView.nationalContractReferenceTab.importModal.confirmButton'),
            onClick: handleConfirm,
            color: 'success',
            icon: 'check_stroke',
            disabled: isConfirmDisabled,
            tooltip: confirmDisabledTooltip,
            isProcessing: { active: isConfirmingImportData },
          },
        ],
      }}
    >
      <PerText className="import-excel-modal-description" mb="m">
        {t('companySettingsView.nationalContractReferenceTab.importModal.topDisclaimer')}
      </PerText>

      <PerText fontSize="s" fontWeight="bold" mb="xxs">
        {t('companySettingsView.nationalContractReferenceTab.importModal.scopeLabel')}
      </PerText>

      <Select
        allowClear
        style={{ width: '100%' }}
        placeholder={t('companySettingsView.nationalContractReferenceTab.importModal.scopePlaceholder')}
        value={selectedScope}
        onChange={(value) => handleScopeChange(value as NationalContractReferenceImportScope | undefined)}
      >
        <Option value={NATIONAL_CONTRACT_REFERENCE_IMPORT_ALL}>
          {t('companySettingsView.nationalContractReferenceTab.importModal.allTypes')}
        </Option>
        {references.map((reference) => (
          <Option key={reference.type} value={reference.type}>
            {t(`employee.nationalContract.type.${reference.type}`)}
          </Option>
        ))}
      </Select>

      <PerSpacer mb="m" />

      {selectedScope && (
        <>
          <PerText className="import-excel-modal-description" mb="m">
            <Trans
              i18nKey="companySettingsView.nationalContractReferenceTab.importModal.uploadBeforeText"
              components={[
                <span
                  key="download-template-link"
                  style={{ textDecoration: 'underline', cursor: 'pointer', display: 'inline', color: '#05cbce' }}
                  onClick={handleDownloadTemplate}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault()
                      handleDownloadTemplate()
                    }
                  }}
                />,
              ]}
            />
          </PerText>

          {(!dropZoneProcessing.active || dropZoneProcessing.errorMessage) && (
            <PerDropzone
              onFileDropped={(file: unknown): void => {
                setDropZoneError(undefined)
                onDrop(file)
              }}
              maxAllowedSize={MAX_ALLOWED_FILE_SIZE}
              footerText={t('companySettingsView.nationalContractReferenceTab.importModal.formatMessage')}
              footerTextClassNames={['import-excel-modal-footer-text']}
              width="50%"
              errorText={dropZoneError}
              errorTextClassNames={['import-excel-modal-error-text']}
            />
          )}

          {(dropZoneProcessing.active || dropZoneProcessing.isProcessing) && (
            <div ref={statusBoxRef}>
              <div
                className={`per-hr-support-national-contract-reference-import-modal__status${
                  dropZoneProcessing.errorMessage
                    ? ' per-hr-support-national-contract-reference-import-modal__status--error'
                    : ' per-hr-support-national-contract-reference-import-modal__status--success'
                }`}
              >
                <PerFlexRow align="middle" direction="row">
                  <Col>
                    <PerFlexRow align="middle" justify="center" width="100%" className="fullHeight">
                      {dropZoneProcessing.isProcessing ? (
                        <Spin spinning />
                      ) : (
                        <IconIndicator
                          icon={dropZoneProcessing.errorMessage ? 'warning_fill' : 'check_stroke'}
                          iconSize={12}
                          background={dropZoneProcessing.errorMessage ? 'error' : 'success'}
                          size="l"
                        />
                      )}
                    </PerFlexRow>
                  </Col>
                  <Col span={20} style={{ padding: 0, marginLeft: '16px' }}>
                    {dropZoneProcessing.errorMessage && (
                      <PerFlexRow direction="column">
                        <PerText fontSize="s">{t('companySettingsView.nationalContractReferenceTab.importModal.errors.message1')}</PerText>
                        <PerText fontSize="s">{dropZoneProcessing.errorMessage}</PerText>
                        <PerText fontSize="s">{t('companySettingsView.nationalContractReferenceTab.importModal.errors.message2')}</PerText>
                      </PerFlexRow>
                    )}
                    {!dropZoneProcessing.errorMessage && (
                      <PerFlexRow direction="column">
                        <PerText fontSize="s" mb="xxs">
                          {dropZoneProcessing.text}
                        </PerText>
                        <PerText appearance="label-small">{filename}</PerText>
                      </PerFlexRow>
                    )}
                  </Col>
                </PerFlexRow>
              </div>
            </div>
          )}
        </>
      )}
    </PerModal>
  )
}
